import React, { useContext, useMemo } from 'react';
import { ChartContainer } from './PPAInsightPieCharts.style';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { AppContext } from '../../../../../../Context/AppContext';
import { NCF_DASHBOARD_QTY_DECIMAL_PLACES } from '../../../../../../api/constants';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#8dd1e1'];

// Common component that accepts tradeName as a prop
type PptInScopeNcfCatgChartProps = {
  tradeName: string;
};

export const PptInScopeNcfCatgChart: React.FC<PptInScopeNcfCatgChartProps> = ({ tradeName }) => {
  const { state: pptState } = useContext(AppContext).pptReports;
  const chartData = useMemo(() => {
    if (!pptState?.pptNcfCategories || pptState.pptNcfCategories.length === 0) {
      return [];
    }

    const filteredData = pptState.pptNcfCategories.filter(
      (category) => category.tradeName === tradeName,
    );
    const total = filteredData.reduce((sum, category) => sum + category.quantity, 0);
    return filteredData
      .reduce((acc, category) => {
        const existing = acc.find((item) => item.name === category.ncfCategory);
        if (existing) {
          existing.y += category.quantity; // Sum up the quantity for duplicate keys
        } else {
          acc.push({ name: category.ncfCategory, y: category.quantity });
        }
        return acc;
      }, [] as { name: string; y: number }[])
      .map((item) => {
        return {
          ...item,
          item_percentage: ((item.y / total) * 100).toFixed(NCF_DASHBOARD_QTY_DECIMAL_PLACES), // Calculate percentage, 3 decimal rounded
        };
      });
  }, [pptState?.pptNcfCategories, tradeName]);

  const pieChartData = useMemo(
    () =>
      chartData.map((item) => ({
        name: item.name,
        value: item.y,
        percentage: parseFloat(item.item_percentage),
      })),
    [chartData],
  );

  return (
    <ChartContainer>
      {chartData.length > 0 ? (
        <>
          <div style={{ width: '100%', height: '400px' }}>
            <h3 style={{ textAlign: 'left', marginBottom: '0.5rem' }}>
              {tradeName} - In Scope NCF Category
            </h3>
            <p style={{ textAlign: 'left', fontSize: '0.875rem', color: '#666', marginBottom: '1rem' }}>
              An overview of In Scope NCF Category for {tradeName}
            </p>
            <ResponsiveContainer width="100%" height="80%">
              <PieChart>
                <Pie
                  data={pieChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={true}
                  label={({ name, percent }) => `${name}: ${((percent ?? 0) * 100).toFixed(3)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {pieChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value, name, props) => [
                    `${Number(value ?? 0).toFixed(3)} (${props.payload.percentage.toFixed(3)}%)`,
                    name
                  ]}
                />
                <Legend verticalAlign="top" height={36} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div
            style={{ display: 'none' }}
            data-testid='report-breakdown-inScopeNcfCategory-chart-hidden-for-automation'
          >
            {JSON.stringify(pieChartData)}
          </div>
        </>
      ) : (
        <div>No data available for {tradeName}</div>
      )}
    </ChartContainer>
  );
};
